import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable, forkJoin, of, zip } from 'rxjs';
import { map, concatMap } from 'rxjs/operators';

import { CrpRole, Crp } from 'modules/shared/models/crp.models';
import { RecordType } from 'modules/shared/models/record.models';
import { CreateOrUpdateCrpPayload, CreateOrUpdateCrpRequest } from 'store/models/customer.models';
import { CrpRolesResponse, CrpRolesRequest } from 'store/models/crp.models';

import { RecordService } from './record.service';

import { environment } from 'environment/environment';

@Injectable()
export class CrpService {

  private apiBase: string = environment.API_BASE;

  constructor(private recordService: RecordService,
              private httpClient: HttpClient) { }

  /**
   * Use to filter which crps have not allowed to do screening.
   * @method getSearchableCrps
   */
  getSearchableCrps(crps: Crp[]): Observable<Crp[]> {
    if (!crps) {
      return of([]);
    }
    return forkJoin(crps
      .filter((crp: Crp) => crp.status === 'CURRENT')
      .map((crp: Crp) => {
        if (crp.recordType === RecordType.CORPORATE) {
          return this.recordService.isIncorporated(crp.record)
            .pipe(map((isCorporated: boolean) => {
              return isCorporated ? crp : null;
            }));
        }
        return of(crp);
      })
    ).pipe(
      map((finalCrps: Crp[]) => {
        return finalCrps.filter((crp: Crp) => crp !== null);
      })
    );
  }

  /**
   * Create or update CRP info
   * @method createOrUpdateCrp
   */
  createOrUpdateCrp(createOrUpdateCrpPayload: CreateOrUpdateCrpPayload): Observable<Crp> {
    let url = this.apiBase;

    if (createOrUpdateCrpPayload.crpData.individualRecord) {
      createOrUpdateCrpPayload.crpData.individualRecord = this.deleteNullContent(createOrUpdateCrpPayload.crpData.individualRecord);
    } else if (createOrUpdateCrpPayload.crpData.corporateRecord) {
      createOrUpdateCrpPayload.crpData.corporateRecord = this.deleteNullContent(createOrUpdateCrpPayload.crpData.corporateRecord);
    }

    if (createOrUpdateCrpPayload.mode === 'CREATE') {
      url += `/api/customers/${createOrUpdateCrpPayload.customer.id}/crps/`;
      return this.httpClient.post<Crp>(url, this.deleteNullContent(createOrUpdateCrpPayload.crpData));
    } else {
      url += `/api/crps/${createOrUpdateCrpPayload.crpId}/`;
      return this.httpClient.patch<Crp>(url, this.deleteNullContent(createOrUpdateCrpPayload.crpData));
    }
  }

  /**
   * Create or update CRP imported from Handshakes
   * @method createOrUpdateCrpFromHandshake
   */
  createOrUpdateCrpFromHandshake(createOrUpdateCrpPayload: CreateOrUpdateCrpPayload): Observable<Crp> {
    let url = this.apiBase;
    let deletedRoles$: Observable<any>;

    if (createOrUpdateCrpPayload.deletedRoles && createOrUpdateCrpPayload.deletedRoles.length > 0) {
      deletedRoles$ = zip(...createOrUpdateCrpPayload.deletedRoles.map(roleId => {
        return this.httpClient.delete<CrpRole>(`${this.apiBase}/api/roles/${roleId}/`);
      }));
    } else {
      deletedRoles$ = of([]);
    }

    return deletedRoles$.pipe(
      concatMap((deletedRoles: Array<any>) => {
        if (createOrUpdateCrpPayload.mode === 'CREATE') {
          url += `/api/customers/${createOrUpdateCrpPayload.customer.id}/crps/`;
          return this.httpClient.post<Crp>(url, this.deleteNullContent(this.populateDefaultValue(createOrUpdateCrpPayload.crpData)));
        } else {
          url += `/api/crps/${createOrUpdateCrpPayload.crpId}/`;
          return this.httpClient.patch<Crp>(url, this.deleteNullContent(this.populateDefaultValue(createOrUpdateCrpPayload.crpData)));
        }
      })
    );
  }

  /**
   * Get all roles of a CRP
   * @method getCrpRoles
   */
  getCrpRoles(getCrpRolesRequestBody: CrpRolesRequest): Observable<CrpRolesResponse> {
    let params: HttpParams = new HttpParams();
    const url = `${this.apiBase}/api/crps/${getCrpRolesRequestBody.crpId}/roles/`;

    if (getCrpRolesRequestBody) {
      const reqBodyEntries: Array<[string, any]> = Object.entries(getCrpRolesRequestBody);

      reqBodyEntries.forEach(([key, value]: [string, any]) => {
        if (value !== undefined) {
          params = params.append(key, value);
        }
      });
    }

    return this.httpClient.get<CrpRolesResponse>(url, { params: params });
  }

  /**
   * Add a CRP role
   * @method addCrpRole
   */
  addCrpRole(crpRole: CrpRole): Observable<CrpRole> {
    const url = `${this.apiBase}/api/crps/${crpRole.crp}/roles/`;
    return this.httpClient.post<CrpRole>(url, crpRole);
  }

  /**
   * Delete a CRP
   * @method deleteCrp
   */
  deleteCrp(crpId: number): Observable<any> {
    return this.httpClient.delete(`${this.apiBase}/api/crps/${crpId}/`);
  }

  /**
   * Delete a CRP role
   * @method deleteRole
   */
  deleteRole(roleId: number): Observable<any> {
    return this.httpClient.delete(`${this.apiBase}/api/roles/${roleId}/`);
  }

  /**
   * Update a CRP role
   * @method updateCrpRole
   */
  updateCrpRole(crpRole: CrpRole): Observable<CrpRole> {
    return this.httpClient.put<CrpRole>(`${this.apiBase}/api/roles/${crpRole.id}/`, crpRole);
  }

  /**
   * Remove null content in payload
   * @method deleteNullContent
   */
  deleteNullContent(object: any): any {
    for (const i in object) {
      if (object[i] === null &&
        !~['idExpiryDate', 'idIssueDate', 'dateOfBirth', 'incorporationDate', 'incorporationExpiryDate'].indexOf(i)
      ) {
        object[i] = '';
      }
    }
    return object;
  }

  /**
   * Set to default value if the value is empty, for now it's only for Handshakes
   * @method populateDefaultValue
   */
  populateDefaultValue(crpData: CreateOrUpdateCrpRequest): CreateOrUpdateCrpRequest {
    crpData.status = 'CURRENT';

    if (crpData.individualRecord) {
      crpData.individualRecord.gender = crpData.individualRecord.gender || 'UNKNOWN';
      crpData.individualRecord.nationality = crpData.individualRecord.nationality || 'UNKNOWN';
      crpData.individualRecord.countryOfResidence = crpData.individualRecord.countryOfResidence || 'UNKNOWN';
      crpData.individualRecord.industry = crpData.individualRecord.industry || 'UNKNOWN - UNKNOWN';
      crpData.individualRecord.occupation = crpData.individualRecord.occupation || 'UNKNOWN - UNKNOWN';
      crpData.individualRecord.onboardingMode = crpData.individualRecord.onboardingMode || 'UNKNOWN';
      crpData.individualRecord.productServiceComplexity = crpData.individualRecord.productServiceComplexity || 'UNKNOWN';
      crpData.individualRecord.paymentModes = crpData.individualRecord.paymentModes || ['UNKNOWN'];
    }

    if (crpData.corporateRecord) {
      crpData.corporateRecord.isIncorporated = crpData.corporateRecord.isIncorporated || false;
      crpData.corporateRecord.entityType = crpData.corporateRecord.entityType || 'UNKNOWN';
      crpData.corporateRecord.countryOfIncorporation = crpData.corporateRecord.countryOfIncorporation || 'UNKNOWN';
      crpData.corporateRecord.countryOfOperations = crpData.corporateRecord.countryOfOperations || 'UNKNOWN';
      crpData.corporateRecord.ownershipStructureLayers = crpData.corporateRecord.ownershipStructureLayers || 'UNKNOWN';
      crpData.corporateRecord.onboardingMode = crpData.corporateRecord.onboardingMode || 'UNKNOWN';
      crpData.corporateRecord.productServiceComplexity = crpData.corporateRecord.productServiceComplexity || 'UNKNOWN';
      crpData.corporateRecord.paymentModes = crpData.corporateRecord.paymentModes || ['UNKNOWN'];
    }

    return crpData;
  }
}

